import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { submitLead } from "../lib/api";
import { Loader2, CheckCircle2, Mail } from "lucide-react";
import { toast } from "sonner";

export const SALES_EMAIL = "sales@getassortiq.com";

const EMPTY = { name: "", email: "", company: "", phone: "", message: "" };

export default function ContactSalesDialog({ open, onOpenChange, source = "website", vertical, tier, title = "Talk to sales" }) {
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const close = (v) => {
    onOpenChange(v);
    if (!v) setTimeout(() => { setForm(EMPTY); setDone(false); }, 200);
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await submitLead({ ...form, source, vertical, tier });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send your request. Please email us directly.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="sm:max-w-md" data-testid="contact-sales-dialog">
        {done ? (
          <div className="py-6 text-center space-y-3" data-testid="contact-sales-success">
            <CheckCircle2 className="h-12 w-12 text-action-keep mx-auto" />
            <h3 className="font-heading text-xl font-bold">Request received</h3>
            <p className="text-sm text-muted-foreground">Our team will reach out within one business day at <span className="font-semibold text-foreground">{form.email}</span>.</p>
            <button data-testid="contact-sales-close" onClick={() => close(false)} className="mt-2 rounded-full bg-ai px-6 py-2 text-sm font-bold uppercase tracking-wider text-white hover:bg-[#e05400]">Done</button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <DialogHeader>
              <DialogTitle className="font-heading">{title}</DialogTitle>
              <DialogDescription>Tell us a little about your operation and we'll set up a tailored walkthrough.</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="lead-name">Name *</Label>
                <Input id="lead-name" data-testid="lead-name" required minLength={2} value={form.name} onChange={set("name")} placeholder="Jane Doe" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="lead-email">Work email *</Label>
                <Input id="lead-email" data-testid="lead-email" type="email" required value={form.email} onChange={set("email")} placeholder="jane@company.com" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="lead-company">Company</Label>
                <Input id="lead-company" data-testid="lead-company" value={form.company} onChange={set("company")} placeholder="Acme Foods" />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="lead-phone">Phone</Label>
                <Input id="lead-phone" data-testid="lead-phone" value={form.phone} onChange={set("phone")} placeholder="+1 555 000 0000" />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lead-message">What are you looking to solve?</Label>
              <Textarea id="lead-message" data-testid="lead-message" rows={3} value={form.message} onChange={set("message")} placeholder="Store count, categories, timeline…" />
            </div>
            <button type="submit" data-testid="lead-submit" disabled={busy}
              className="w-full flex items-center justify-center gap-2 rounded-full bg-ai py-2.5 text-sm font-bold uppercase tracking-wider text-white hover:bg-[#e05400] transition-all disabled:opacity-60">
              {busy && <Loader2 className="h-4 w-4 animate-spin" />} {busy ? "Sending…" : "Request a call"}
            </button>
            <p className="text-center text-xs text-muted-foreground flex items-center justify-center gap-1.5">
              <Mail className="h-3.5 w-3.5" /> Prefer email? <a data-testid="lead-mailto" href={`mailto:${SALES_EMAIL}`} className="underline hover:text-foreground">{SALES_EMAIL}</a>
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
